import {
  addAnomalyPayload,
  AnomalyAPI,
  AnomalyTypeAPI,
  CommentAPI,
  currentDateTimeInMs,
  RegionAPI,
  ScreenshotClocks,
  StatusesAPI,
  updateAnomalyPayload,
} from '@shared';
import { API_ROUTES, request } from './instance';

// swagger https://qa-auditor.proctorfree.com/swagger-ui/#/audit-api-controller

export const getSessionReview = (session: string) => {
  return request<SessionReviewAPI>({
    url: `${API_ROUTES.apiV1Audit(session)}`,
    method: 'GET',
  });
};

export const getProcessingRegions = (session: string) => {
  return request<RegionAPI[]>({
    url: `${API_ROUTES.apiV1AuditProcessingRegions(session)}`,
    method: 'GET',
  });
};

export const getTargetRegions = (session: string) => {
  return request<RegionAPI[]>({
    url: `${API_ROUTES.apiV1AuditTargetRegions(session)}`,
    method: 'GET',
  });
};

export const getSuspectRegions = (session: string) => {
  return request<RegionAPI[]>({
    url: `${API_ROUTES.apiV1AuditSuspectRegions(session)}`,
    method: 'GET',
  });
};

export const getStatuses = (session: string) => {
  return request<StatusesAPI>({
    url: `${API_ROUTES.apiV1AuditStatuses(session)}`,
    method: 'GET',
  });
};

export const updateStatuses = (session: string, payload: Partial<StatusesAPI>) => {
  return request<StatusesAPI>({
    url: `${API_ROUTES.apiV1AuditStatuses(session)}`,
    method: 'PUT',
    data: payload,
  });
};

export const getComments = (session: string) => {
  return request<CommentAPI[]>({
    url: `${API_ROUTES.apiV1AuditComments(session)}`,
    method: 'GET',
  });
};

export const addComment = (session: string, comment: string) => {
  return request<CommentAPI>({
    url: `${API_ROUTES.apiV1AuditComments(session)}`,
    method: 'POST',
    data: {
      comment,
      timestamp: currentDateTimeInMs(),
    },
  });
};

export const finishAudit = (session: string) => {
  return request({
    url: `${API_ROUTES.apiV1AuditFinish(session)}`,
    method: 'POST',
  });
};

export const getAnomalyTypes = () => {
  return request<AnomalyTypeAPI[]>({
    url: `${API_ROUTES.apiV1AnomalyTypes()}`,
    method: 'GET',
  });
};

export const getAnomalies = (auditUUID: string) => {
  return request<AnomalyAPI[]>({
    url: `${API_ROUTES.apiV1Anomalies(auditUUID)}`,
    method: 'GET',
  });
};

export const addAnomaly = (session: string, payload: addAnomalyPayload) => {
  const { id, comment, startOffset, endOffset } = payload;
  const data = () => {
    const requiredData = {
      anomalyType: { id: id },
      startOffset,
      endOffset,
      ...(comment && { comments: [{ comment, timestamp: currentDateTimeInMs() }] }),
    };
    const optionalData = { region: { uuid: payload.regionUuid } };
    return payload.regionUuid ? Object.assign(requiredData, optionalData) : requiredData;
  };
  return request<AnomalyAPI>({
    url: `${API_ROUTES.apiV1Anomalies(session)}`,
    method: 'POST',
    data: data(),
  });
};

export const updateAnomaly = (
  session: string,
  anomalyUuid: string,
  payload: updateAnomalyPayload
) => {
  const { id, startOffset } = payload;
  return request<AnomalyAPI>({
    url: `${API_ROUTES.apiV1Anomaly(session, anomalyUuid)}`,
    method: 'PUT',
    data: {
      anomalyType: { id: id },
      startOffset,
    },
  });
};

export const deleteAnomaly = (session: string, anomalyUuid: string) => {
  return request<AnomalyAPI[]>({
    url: `${API_ROUTES.apiV1Anomaly(session, anomalyUuid)}`,
    method: 'DELETE',
  });
};

export const getRejectedRegions = (session: string) => {
  return request<RegionAPI[]>({
    url: `${API_ROUTES.apiV1AuditRejectedRegions(session)}`,
    method: 'GET',
  });
};

export const fetchTraceUrl = (traceHandle: string) => {
  return request<string>({
    url: `${API_ROUTES.apiV1AuditVideoURL(traceHandle)}`,
    method: 'GET',
  });
};

export const rejectRegion = (session: string, regionUuid: string) => {
  return request<RegionAPI>({
    url: `${API_ROUTES.apiV1AuditRejectTargetRegion(session, regionUuid)}`,
    method: 'PUT',
  });
};

export const addCommentToAnomaly = (session: string, anomalyUuid: string, comment: string) => {
  return request<CommentAPI>({
    url: `${API_ROUTES.apiV1AuditAnomalyComments(session, anomalyUuid)}`,
    method: 'POST',
    data: {
      comment,
      timestamp: currentDateTimeInMs(),
    },
  });
};

export type SessionReviewAPI = {
  examSessionStartTime: number;
  examName: string;
  partnerName: string;
  partnerUuid: string;
  specialPartner: boolean;
  auditUuid: string;
  stream: {
    traces: Array<{
      handle: string;
    }>;
    handle: string;
    videoEndClock: number;
    videoStartClock: number;
  };
  screenshotsClock: ScreenshotClocks;
  profileTrace: {
    handle: string;
  };
  documentTrace: {
    handle: string;
  };
  roomScanTrace: {
    handle: string;
  };
};
